import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemButton,
} from "@mui/material";
import {
  fetchCustomers,
  fetchLogs,
  Log,
} from "../../store/slice/ProjectTasksSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { debounce } from "lodash";

export interface Customer {
  userId: string;
  userName: string;
}

const UserManagement: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { customers, logs, loadingLog } = useSelector(
    (state: RootState) => state.Tasks
  );

  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[] | null>(
    []
  );
  const [selectedUserData, setSelectedUserData] = useState<Log[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

  /*   useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]); */

  /*   useEffect(() => {
    setFilteredCustomers(customers);
    if (customers.length > 0) {
      setSelectedCustomer(customers[0].userId);
      setSearchTerm(customers[0].userName);
    }
  }, [customers]); */

  /*   useEffect(() => {
    if (selectedCustomer) {
      console.log("selectedCustomer", selectedCustomer);
      dispatch(fetchLogs(selectedCustomer));
    }
  }, [selectedCustomer, dispatch]); */

  const formatDateWithStaticTime = (
    dateString: string | null
  ): string | null => {
    if (!dateString) return null;

    // Create date object from input (local time)
    const date = new Date(dateString);

    // Get date components in local time
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    // Format as YYYY-MM-DD 00:00:00.000000
    return `${year}-${month}-${day} 00:00:00.000000`;
  };

  const handleSearchButton = () => {
    // Clear previous errors
    setError({ start: "", end: "" });

    // Validate dates
    if (dates.start && dates.end) {
      const startDate = new Date(dates.start);
      const endDate = new Date(dates.end);
      const today = new Date(getTodayDate());

      if (startDate > endDate) {
        setError({
          start: "Start date cannot be after end date",
          end: "End date cannot be before start date",
        });
        return;
      }

      if (endDate > today) {
        setError({
          start: "",
          end: "End date cannot be in the future",
        });
        return;
      }
    }

    dispatch(
      fetchLogs({
        userId: selectedCustomer,
        startDate: formatDateWithStaticTime(dates.start),
        endDate: formatDateWithStaticTime(dates.end),
      })
    );
  };
  const handleResetButton = () => {
    setSelectedCustomer("");
    setSearchTerm("");
    setDates({ start: null, end: null });
  };

  const convertToReadableDate = (timestamp: any) => {
    return new Date(timestamp).toLocaleDateString();
  };

  useEffect(() => {
    setSelectedUserData(logs);
  }, [logs]);

  // Debounced Search Function
  const handleSearch = useCallback(
    debounce(async (value: string) => {
      setLoading(true);

      // Fetch new customer data
      const response = await dispatch(fetchCustomers(value)).unwrap();

      // Filter the updated customer list
      const filteredResults = response.filter((customer: any) =>
        customer.userName.toLowerCase().includes(value.toLowerCase())
      );

      setFilteredCustomers(filteredResults);
      setLoading(false);
    }, 500),
    [dispatch]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setLoading(true);
    setShowSuggestions(true);
    handleSearch(value);
  };

  // ✅ On Click, Select Customer ID & Name
  const handleSelectCustomer = (
    customer: Customer,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Stop event bubbling
    console.log("Clicked Customer:", customer); // Debugging
    setSelectedCustomer(customer.userId);
    setSearchTerm(customer.userName);
    setShowSuggestions(false);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200); // Delay allows the click event to register
  };

  const [dates, setDates] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null,
  });

  const [error, setError] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });

  // Function to validate date format (dd-mm-yyyy)
  /*   const validateDate = (value: string): boolean => {
    const datePattern = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    return datePattern.test(value);
  }; */

  // Handle input change
  const handleChange =
    (type: "start" | "end") => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const today = getTodayDate();

      if (type === "start") {
        // For start date, ensure it's not after the current end date (if one exists)
        const maxDate = dates.end || today;
        const newDate = value > maxDate ? maxDate : value;
        setDates((prev) => ({ ...prev, start: newDate }));
      } else {
        // For end date, ensure it's not before the start date (if one exists)
        // and not after today
        const minDate = dates.start || undefined;
        let newDate = value;

        if (minDate && newDate < minDate) {
          newDate = minDate;
        }
        if (newDate > today) {
          newDate = today;
        }

        setDates((prev) => ({ ...prev, end: newDate }));
      }
    };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <Box sx={{ background: "#F5F6F9", padding: "20px" }}>
      <Box
        sx={{
          boxShadow: "0px 0px 5px 0px #0000003",
          background: "white",
          padding: "10px",
          position: "relative", // Add this to make the Paper position relative to this Box
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: "20px" }}>
          User Management
        </Typography>
        <Box sx={{ display: "flex" }}>
          <TextField
            label="Search Customer"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            sx={{ marginBottom: "10px", minWidth: 200 }}
          />
          <div>
            <input
              type="date"
              value={dates.start ?? ""}
              onChange={handleChange("start")}
              max={dates.end || getTodayDate()}
              style={{
                height: "22px",
                fontSize: "16px",
                padding: "8px",
                marginLeft: "8px",
                borderRadius: "5px",
                border: error.start ? "1px solid red" : "1px solid #ccc",
              }}
            />
            <br />
            {error && <span style={{ color: "red" }}>{error.start}</span>}
          </div>
          <div>
            <input
              type="date"
              value={dates.end ?? ""}
              onChange={handleChange("end")}
              min={dates.start || undefined}
              max={getTodayDate()}
              style={{
                height: "22px",
                fontSize: "16px",
                padding: "8px",
                marginLeft: "8px",
                borderRadius: "5px",
                border: error.end ? "1px solid red" : "1px solid #ccc",
              }}
            />
            <br />
            {error && <span style={{ color: "red" }}>{error.end}</span>}
          </div>
          <div>
            <button
              onClick={handleSearchButton}
              disabled={
                !selectedCustomer ||
                (dates.start !== null && dates.end === null)
              }
              style={{
                height: "40px",
                fontSize: "16px",
                padding: "8px",
                marginLeft: "8px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                background:
                  !selectedCustomer ||
                  (dates.start !== null && dates.end === null)
                    ? "#b0bec5"
                    : "#1565c0", // Disabled = Grey, Active = Orange
                color: "#FFF",
                cursor:
                  !selectedCustomer ||
                  (dates.start !== null && dates.end === null)
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  !selectedCustomer ||
                  (dates.start !== null && dates.end === null)
                    ? 0.6
                    : 1, // Reduce opacity when disabled
              }}
            >
              Search
            </button>
          </div>
          <div>
            <button
              onClick={() => handleResetButton()}
              style={{
                height: "40px", // Increase height
                fontSize: "16px", // Make text bigger
                padding: "8px", // Add some padding
                marginLeft: "8px",
                borderRadius: "5px", // Rounded corners
                border: "1px solid #ccc", // Light border
                background: "#FF1C379C",
                color: "#FFF",
              }}
            >
              Reset
            </button>
          </div>
        </Box>
        {/* Show List Only on Focus */}
        {showSuggestions && (
          <Paper
            sx={{
              maxHeight: 250,
              overflow: "auto",
              mt: 1,
              maxWidth: 250,
              minWidth: 250,
              position: "absolute", // Make the Paper absolutely positioned
              zIndex: 1, // Ensure it appears above other elements
              backgroundColor: "white", // Ensure background is white
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Optional: Add shadow for better visibility
            }}
          >
            <List>
              {loadingLog ? (
                <ListItem>
                  <CircularProgress size={24} />
                </ListItem>
              ) : filteredCustomers && filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <ListItem key={customer.userId} disablePadding>
                    <ListItemButton
                      onMouseDown={(event) => event.preventDefault()} // Prevent focus loss
                      onClick={(event) => handleSelectCustomer(customer, event)}
                    >
                      <ListItemText primary={customer.userName} />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No results found" />
                </ListItem>
              )}
            </List>
          </Paper>
        )}

        {/* User Details Table */}
        <TableContainer
          component={Paper}
          sx={{ marginTop: "20px", maxHeight: "75vh" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Loading ...
                  </TableCell>
                </TableRow>
              ) : selectedUserData.length > 0 ? (
                selectedUserData.map((log, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{log.userName}</TableCell>
                    <TableCell>{log.activity}</TableCell>
                    <TableCell>
                      {convertToReadableDate(log.createdDate)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No activity logs available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default UserManagement;
